import Image, { type ImageProps } from "next/image";

/* ──────────────────────────────────────────────────────────
   ThemedCapture — one product screenshot, two themes.

   The marketing captures in /public/product/sidebar-*.png are
   real shots of the app sidebar, and the app has a light and a
   dark theme — so every capture ships twice:
     sidebar-<layer>.png        dark
     sidebar-<layer>-light.png  light
   both cut by scripts/capture-product-sidebar.ts from the SAME
   property, scope and session, so the two themes never show two
   different products (§Marketing claims).

   Both <img>s render; globals.css hides the one that doesn't
   match `data-theme` on <html>. That keeps this server-safe (no
   hook, no theme flash on a light-mode first paint) and, because
   both are lazy, the hidden one is never fetched — a display:none
   lazy image is not in any viewport.
────────────────────────────────────────────────────────── */

export type ThemedCaptureProps = Omit<ImageProps, "src"> & {
  /** The DARK capture path, e.g. "/product/sidebar-overview.png". */
  src: string;
};

/** "/product/sidebar-x.png" → "/product/sidebar-x-light.png" */
export function lightCaptureSrc(darkSrc: string): string {
  return darkSrc.replace(/\.(png|jpg|jpeg|webp)$/i, "-light.$1");
}

export default function ThemedCapture({ src, className = "", loading = "lazy", ...rest }: ThemedCaptureProps) {
  return (
    <>
      <Image {...rest} src={src} loading={loading} className={`capture-dark ${className}`} />
      <Image {...rest} src={lightCaptureSrc(src)} loading={loading} className={`capture-light ${className}`} />
    </>
  );
}
